import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
    UserIcon,
    PencilIcon,
    XMarkIcon,
    CheckIcon,
    CurrencyRupeeIcon,
    ClockIcon,
    DocumentTextIcon,
    ChartBarIcon,
    IdentificationIcon,
    EnvelopeIcon,
    PhoneIcon,
    HomeIcon,
    ArrowPathIcon,
    LinkIcon,
    CreditCardIcon,
    ShieldCheckIcon,
    UserGroupIcon,
    ChartPieIcon,
    EllipsisVerticalIcon,
    FunnelIcon,
    ChevronDownIcon,
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XCircleIcon,
    BanknotesIcon,
    WalletIcon,
    ExclamationTriangleIcon,
    ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@headlessui/react';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL;

const OrderDetails = ({ children }) => {
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('orderId');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderData, setOrderData] = useState({
        order_info: {},
        ledger_info: [],
        logs: []
    });
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        status: '',
        transaction_id: ''
    });
    const navigate = useNavigate();
    const [access_data, setAccess_data] = useState([]);
    const [editFormErrors, setEditFormErrors] = useState({
        status: '',
        transaction_id: ''
    });

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, []);

    useEffect(() => {
        if (access_data?.username && orderId) {
            fetchOrderData();
        }
    }, [access_data?.username, orderId]);

    const fetchOrderData = async () => {
        setLoading(true);
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'order-details',
                    order_id: orderId
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            const response = await fetch(`${API_URL}order-details.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setOrderData({
                    order_info: result.order_info || {},
                    ledger_info: result.ledger_info || [],
                    logs: result.logs || [],
                    complaint: result.complaint || []
                });
                setEditFormData({
                    status: result.order_info?.status || '',
                    transaction_id: result.order_info?.transaction_id || ''
                });
            } else {
                toast.error(result.message || "Failed to fetch order data");
            }
        } catch (error) {
            console.error("Error fetching order data:", error);
            toast.error("An error occurred while fetching order data");
        } finally {
            setLoading(false);
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            status: '',
            transaction_id: ''
        };

        if (!editFormData.status || editFormData.status === "") {
            newErrors.status = 'Status is required';
            valid = false;
        }

        if (!editFormData.transaction_id) {
            newErrors.transaction_id = 'Transaction ID is required';
            valid = false;
        }

        setEditFormErrors(newErrors);
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'update-order',
                    order_id: orderId,
                    status: editFormData.status,
                    transaction_id: editFormData.transaction_id
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            const response = await fetch(`${API_URL}order-update.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                toast.success("Order updated successfully");
                setOrderData(prev => ({
                    ...prev,
                    order_info: {
                        ...prev.order_info,
                        status: editFormData.status,
                        transaction_id: editFormData.transaction_id,
                        last_update: new Date().toISOString()
                    }
                }));
                setEditModalOpen(false);
            } else {
                toast.error(result.message || "Failed to update order");
            }
        } catch (error) {
            console.error("Error updating order:", error);
            toast.error("An error occurred while updating order");
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleMobileSidebar = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case '1':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case '2':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case '3':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case '0':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            default:
                return 'bg-red-100 text-red-800 dark:bg-red-700 dark:text-red-300';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case '1':
                return 'Success';
            case '2':
                return 'Failed';
            case '3':
                return 'Pending';
            case '0':
                return 'Processing';
            default:
                return 'Error';
        }
    };

    const getLogType = (category) => {
        switch (category) {
            case '2':
                return {
                    text: 'Recharge/Bill Payment',
                    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                };
            case '0':
                return {
                    text: 'Status Check',
                    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                };
            default:
                return {
                    text: 'Error',
                    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString.includes('-0001')) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatLogResponse = (response) => {
        try {
            const parsed = JSON.parse(response);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return response;
        }
    };

    const handleRecheck = async () => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    order_id: orderId
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                setLoading(false);
                return;
            }

            const payloadToSend = {
                encrypted_key: encryptedMessage.encryptedKey,
                encrypted_data: encryptedMessage.encryptedData,
            };

            const response = await fetch(`${API_URL}order-recheck.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify(payloadToSend),
            });

            const result = await response.json();
        } catch (error) {
            toast.error("Failed to refresh status");
        } finally {
            fetchOrderData();
            toast.success("Status refreshed");
        }
    }

    const handleRetrigger = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "Retrigger webhook or fetch status!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    Swal.fire({
                        title: 'Processing...',
                        html: 'Please wait while we process your request',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        allowEnterKey: false,
                        showConfirmButton: false,
                        willOpen: () => {
                            Swal.showLoading();
                        }
                    });

                    const dataToEncrypt = {
                        user_id: access_data?.username || '',
                        login_id: access_data?.login_id || '',
                        device_id: getDeviceInfo().deviceId,
                        payload: {
                            order_id: orderId
                        }
                    };

                    const encryptedMessage = encryptDataHybrid(dataToEncrypt);

                    if (!encryptedMessage) {
                        toast.error("Encryption failed. Please try again.");
                        setLoading(false);
                        return;
                    }

                    const payloadToSend = {
                        encrypted_key: encryptedMessage.encryptedKey,
                        encrypted_data: encryptedMessage.encryptedData,
                    };

                    const response = await fetch(`${API_URL}order-retrigger.php`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                        },
                        body: JSON.stringify(payloadToSend),
                    });

                    const result = await response.json();

                    if (result.status === true) {
                        toast.success("Webhook retriggered successfully!");
                    } else {
                        toast.error(result.message || "Failed to retrigger webhook.");
                    }
                } catch (error) {
                    toast.error("An unexpected error occurred while retriggering webhook. Please try again.");
                } finally {
                    Swal.close();
                }
            }
        });
    }

    const copyText = (text, label) => {
        if (!text || text === 'N/A') return;
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    // Skeleton Loader Component
    const SkeletonLoader = () => (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white flex">
            <div className={`flex flex-col flex-1 transition-all duration-300 ease-in-out md:ml-20`}>
                <Header />
                <main className="flex-1 p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        {/* Order Header Skeleton */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6 animate-pulse">
                            <div className="p-6">
                                <div className="h-8 w-1/3 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="space-y-2">
                                                <div className="h-4 w-1/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                                <div className="h-5 w-3/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="space-y-4">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="space-y-2">
                                                <div className="h-4 w-1/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                                <div className="h-5 w-3/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ledger Skeleton */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6 animate-pulse">
                            <div className="p-6">
                                <div className="h-6 w-1/4 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                                <div className="space-y-4">
                                    {[...Array(2)].map((_, i) => (
                                        <div key={i} className="grid grid-cols-5 gap-4">
                                            {[...Array(5)].map((_, j) => (
                                                <div key={j} className="h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Logs Skeleton */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden animate-pulse">
                            <div className="p-6">
                                <div className="h-6 w-1/4 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                                <div className="space-y-4">
                                    {[...Array(1)].map((_, i) => (
                                        <div key={i} className="space-y-2">
                                            <div className="h-4 w-1/3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            <div className="h-20 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );

    if (loading) {
        return <SkeletonLoader />;
    }

    if (!orderData.order_info || Object.keys(orderData.order_info).length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white flex">
                <div className={`flex flex-col flex-1 transition-all duration-300 ease-in-out md:ml-20`}>
                    <Header toggleMobileSidebar={toggleMobileSidebar} />
                    <div className="flex justify-center items-center h-full">
                        <p className="text-gray-600 dark:text-gray-300">Order not found</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
    <div className="flex flex-col flex-1">
        <Header />
        <main className="flex-1 overflow-auto">
            <div className="flex-1 p-4 md:p-6 overflow-auto">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                        >
                            <ChevronLeftIcon className="h-5 w-5 mr-1" />
                            <span className="hidden sm:inline">Back</span>
                        </button>
                        <h2 className="text-xl font-semibold">
                            Order Details
                        </h2>
                        <div></div>
                    </div>
                    {children || (
                        <>
                            {/* Order Header */}
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6">
                                <div className="p-4 md:p-6">
                                    {/* Status Indicator Section */}
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-4 sm:space-y-0">
                                        <div className="flex items-center">
                                            {/* Status Icon */}
                                            <div className="relative mr-4">
                                                <AnimatePresence mode="wait">
                                                    {orderData.order_info.status === 'success' && (
                                                        <motion.div
                                                            key="success"
                                                            initial={{ scale: 0, opacity: 0 }}
                                                            animate={{ scale: 1, opacity: 1 }}
                                                            exit={{ scale: 0, opacity: 0 }}
                                                            className="w-10 h-10 md:w-12 md:h-12"
                                                        >
                                                            <svg className="w-full h-full" viewBox="0 0 100 100">
                                                                <circle cx="50" cy="50" r="45" fill="#10B981" className="opacity-20" />
                                                                <motion.path
                                                                    initial={{ pathLength: 0 }}
                                                                    animate={{ pathLength: 1 }}
                                                                    transition={{ duration: 0.5, delay: 0.2 }}
                                                                    stroke="#10B981"
                                                                    strokeWidth="8"
                                                                    fill="none"
                                                                    d="M30,50 L45,65 L70,35"
                                                                />
                                                            </svg>
                                                        </motion.div>
                                                    )}
                                                    {orderData.order_info.status === 'failed' && (
                                                        <motion.div
                                                            key="failed"
                                                            initial={{ scale: 0, opacity: 0 }}
                                                            animate={{ scale: 1, opacity: 1 }}
                                                            exit={{ scale: 0, opacity: 0 }}
                                                            className="w-10 h-10 md:w-12 md:h-12"
                                                        >
                                                            <svg className="w-full h-full" viewBox="0 0 100 100">
                                                                <circle cx="50" cy="50" r="45" fill="#EF4444" className="opacity-20" />
                                                                <motion.path
                                                                    initial={{ pathLength: 0 }}
                                                                    animate={{ pathLength: 1 }}
                                                                    transition={{ duration: 0.5, delay: 0.2 }}
                                                                    stroke="#EF4444"
                                                                    strokeWidth="8"
                                                                    fill="none"
                                                                    d="M35,35 L65,65"
                                                                />
                                                                <motion.path
                                                                    initial={{ pathLength: 0 }}
                                                                    animate={{ pathLength: 1 }}
                                                                    transition={{ duration: 0.5, delay: 0.2 }}
                                                                    stroke="#EF4444"
                                                                    strokeWidth="8"
                                                                    fill="none"
                                                                    d="M65,35 L35,65"
                                                                />
                                                            </svg>
                                                        </motion.div>
                                                    )}
                                                    {(orderData.order_info.status === 'initiated' || orderData.order_info.status === 'processed') && (
                                                        <motion.div
                                                            key="pending"
                                                            initial={{ scale: 0, opacity: 0 }}
                                                            animate={{ scale: 1, opacity: 1, rotate: 360 }}
                                                            transition={{ rotate: { repeat: Infinity, duration: 1.5, ease: "linear" } }}
                                                            exit={{ scale: 0, opacity: 0 }}
                                                            className="w-10 h-10 md:w-12 md:h-12"
                                                        >
                                                            <svg className="w-full h-full" viewBox="0 0 100 100">
                                                                <circle cx="50" cy="50" r="45" fill="#3B82F6" className="opacity-20" />
                                                                <motion.path
                                                                    initial={{ pathLength: 0.25 }}
                                                                    animate={{ pathLength: 1 }}
                                                                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                                                                    stroke="#3B82F6"
                                                                    strokeWidth="8"
                                                                    fill="none"
                                                                    strokeLinecap="round"
                                                                    d="M50,5 a45,45 0 0,1 45,45"
                                                                />
                                                            </svg>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>

                                            {/* Operator Logo and Name */}
                                            <div className="flex items-center">
                                                {orderData.order_info.operator_logo && (
                                                    <motion.img
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: 0.3 }}
                                                        src={orderData.order_info.operator_logo}
                                                        alt="Operator Logo"
                                                        className="w-6 h-6 md:w-8 md:h-8 mr-3 rounded"
                                                    />
                                                )}
                                                <div>
                                                    <p className="text-xs md:text-sm font-bold text-gray-700 dark:text-gray-400">{orderData.order_info.biller_name || 'N/A'}</p>
                                                    <p className="text-sm md:text-lg font-semibold text-gray-800 dark:text-white">
                                                        {orderData.order_info.number || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status Badge */}
                                        <motion.span
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(orderData.order_info.status)}`}
                                        >
                                            {getStatusText(orderData.order_info.status)}
                                        </motion.span>
                                    </div>

                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.2 }}
                                        className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 space-y-4 md:space-y-0"
                                    >
                                        <div>
                                            <h1 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">Order Details</h1>
                                            <div className="flex items-center mt-2">
                                                <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">Order ID: {orderData.order_info.order_id}</p>
                                                <button
                                                    onClick={() => copyText(orderData.order_info.order_id, 'Order ID')}
                                                    className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                >
                                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                            <div className="flex items-center mt-1">
                                                <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">Biller: {orderData.order_info.biller_name || 'N/A'}</p>
                                                {orderData.order_info.biller_name && orderData.order_info.biller_name !== 'N/A' && (
                                                    <button
                                                        onClick={() => copyText(orderData.order_info.biller_name, 'Biller Name')}
                                                        className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                    >
                                                        <ClipboardDocumentIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {/* Edit - Blue */}
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => setEditModalOpen(true)}
                                                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 flex items-center space-x-1 text-sm"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                                <span>Edit</span>
                                            </motion.button>

                                            {/* Refresh - Green */}
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={fetchOrderData}
                                                className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 flex items-center space-x-1 text-sm"
                                            >
                                                <ArrowPathIcon className="h-4 w-4" />
                                                <span>Refresh</span>
                                            </motion.button>

                                            {/* Retrigger Webhook - Yellow */}
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={handleRetrigger}
                                                className="bg-yellow-500 text-white px-3 py-1 rounded-md hover:bg-yellow-600 flex items-center space-x-1 text-sm"
                                            >
                                                <ArrowPathIcon className="h-4 w-4" />
                                                <span className="hidden sm:inline">Retrigger Webhook</span>
                                                <span className="sm:hidden">Webhook</span>
                                            </motion.button>

                                            {/* Recheck - Red */}
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={handleRecheck}
                                                className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 flex items-center space-x-1 text-sm"
                                            >
                                                <ArrowPathIcon className="h-4 w-4" />
                                                <span>Recheck</span>
                                            </motion.button>
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 }}
                                        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                                    >
                                        {/* Transaction Info */}
                                        <div className="space-y-4">
                                            <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white border-b pb-2">Transaction Information</h2>
                                            <div className="flex items-start">
                                                <IdentificationIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Client Order ID</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200 break-all">{orderData.order_info.client_order_id || 'N/A'}</p>
                                                        {orderData.order_info.client_order_id && orderData.order_info.client_order_id !== 'N/A' && (
                                                            <button
                                                                onClick={() => copyText(orderData.order_info.client_order_id, 'Client Order ID')}
                                                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                            >
                                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <CurrencyRupeeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Amount</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200">₹{parseFloat(orderData.order_info.amount || 0).toFixed(2)}</p>
                                                        <button
                                                            onClick={() => copyText(parseFloat(orderData.order_info.amount || 0).toFixed(2), 'Amount')}
                                                            className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                        >
                                                            <ClipboardDocumentIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <PhoneIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Number</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200">{orderData.order_info.number || 'N/A'}</p>
                                                        {orderData.order_info.number && orderData.order_info.number !== 'N/A' && (
                                                            <button
                                                                onClick={() => copyText(orderData.order_info.number, 'Number')}
                                                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                            >
                                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    </div>
                                            </div>
                                            <div className="flex items-start">
                                                <ShieldCheckIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                                                    <span className={`inline-flex px-2 text-xs font-semibold rounded-full ${getStatusColor(orderData.order_info.status)}`}>
                                                        {getStatusText(orderData.order_info.status)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <LinkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Transaction ID</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200 break-all">{orderData.order_info.transaction_id || 'N/A'}</p>
                                                        {orderData.order_info.transaction_id && orderData.order_info.transaction_id !== 'N/A' && (
                                                            <button
                                                                onClick={() => copyText(orderData.order_info.transaction_id, 'Transaction ID')}
                                                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                            >
                                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* User Info */}
                                        <div className="space-y-4">
                                            <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white border-b pb-2">User Information</h2>
                                            <div className="flex items-start">
                                                <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">User ID</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200 break-all">{orderData.order_info.user_id || 'N/A'}</p>
                                                        {orderData.order_info.user_id && orderData.order_info.user_id !== 'N/A' && (
                                                            <button
                                                                onClick={() => copyText(orderData.order_info.user_id, 'User ID')}
                                                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                            >
                                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">User Name</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200 break-all">{orderData.order_info.user_name || 'N/A'}</p>
                                                        {orderData.order_info.user_name && orderData.order_info.user_name !== 'N/A' && (
                                                            <button
                                                                onClick={() => copyText(orderData.order_info.user_name, 'User Name')}
                                                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                            >
                                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <BanknotesIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">User Profit</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200">₹{parseFloat(orderData.order_info.user_profit || 0).toFixed(2)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <BanknotesIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Admin Profit</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200">₹{parseFloat(orderData.order_info.admin_profit || 0).toFixed(2)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <BanknotesIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Distributor Profit</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200">₹{parseFloat(orderData.order_info.distributer_profit || 0).toFixed(2)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>

                            {/* Additional Details */}
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6">
                                <div className="p-4 md:p-6">
                                    <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white border-b pb-2 mb-4">Additional Details</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Service Information</h3>
                                            <div className="space-y-3">
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Operator ID</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200 break-all">{orderData.order_info.operator_id || 'N/A'}</p>
                                                        {orderData.order_info.operator_id && orderData.order_info.operator_id !== 'N/A' && (
                                                            <button
                                                                onClick={() => copyText(orderData.order_info.operator_id, 'Operator ID')}
                                                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                            >
                                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Category</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200">{orderData.order_info.category || 'N/A'}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Provider ID</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200 break-all">{orderData.order_info.provider_id || 'N/A'}</p>
                                                        {orderData.order_info.provider_id && orderData.order_info.provider_id !== 'N/A' && (
                                                            <button
                                                                onClick={() => copyText(orderData.order_info.provider_id, 'Provider ID')}
                                                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                            >
                                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Convenience Fee</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200">₹{parseFloat(orderData.order_info.convenience || 0).toFixed(2)}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-50 dark:text-gray-400">Biller Category</p>
                                                    <div className="flex items-center">
                                                        <p className="text-gray-800 dark:text-gray-200">{orderData.order_info.biller_category || 'N/A'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Timestamps</h3>
                                            <div className="space-y-3">
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Created Date</p>
                                                    <p className="text-gray-800 dark:text-gray-200 text-sm">
                                                        {formatDate(orderData.order_info.createDate)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Created At</p>
                                                    <p className="text-gray-800 dark:text-gray-200 text-sm">
                                                        {formatDate(orderData.order_info.createAt)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Processed Time</p>
                                                    <p className="text-gray-800 dark:text-gray-200 text-sm">
                                                        {formatDate(orderData.order_info.time)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Last Updated</p>
                                                    <p className="text-gray-800 dark:text-gray-200 text-sm">
                                                        {formatDate(orderData.order_info.last_update)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Ledger Information */}
                            {orderData.ledger_info && orderData.ledger_info.length > 0 && (
                                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6">
                                    <div className="p-4 md:p-6">
                                        <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white border-b pb-2 mb-4">Ledger Transactions</h2>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                <thead className="bg-gray-50 dark:bg-gray-700">
                                                    <tr>
                                                        <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Transaction ID</th>
                                                        <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                                                        <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Debit</th>
                                                        <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Credit</th>
                                                        <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Balance</th>
                                                        <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                    {orderData.ledger_info.map((ledger, index) => (
                                                        <tr key={index}>
                                                            <td className="px-3 py-2 md:px-4 md:py-2 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">{ledger.transaction_id}</td>
                                                            <td className="px-3 py-2 md:px-4 md:py-2 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">{formatDate(ledger.createDate)}</td>
                                                            <td className="px-3 py-2 md:px-4 md:py-2 whitespace-nowrap text-sm text-red-600 dark:text-red-400">₹{parseFloat(ledger.dr_amount || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 md:px-4 md:py-2 whitespace-nowrap text-sm text-green-600 dark:text-green-400">₹{parseFloat(ledger.cr_amount || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 md:px-4 md:py-2 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">₹{parseFloat(ledger.balance || 0).toFixed(2)}</td>
                                                            <td className="px-3 py-2 md:px-4 md:py-2 text-sm text-gray-800 dark:text-gray-200">{ledger.description}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Logs Information */}
                            {orderData.logs && orderData.logs.length > 0 && (
                                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6">
                                    <div className="p-4 md:p-6">
                                        <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white border-b pb-2 mb-4">Transaction Logs</h2>
                                        <div className="space-y-4">
                                            {orderData.logs.map((log, index) => {
                                                const logType = getLogType(log.category);
                                                return (
                                                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                                        <div className={`px-3 py-2 md:px-4 md:py-2 flex flex-col sm:flex-row sm:justify-between sm:items-center ${logType.color}`}>
                                                            <div className="flex items-center">
                                                                <span className="font-medium text-sm">{logType.text}</span>
                                                                {log.company_name && (
                                                                    <span className="ml-2 text-xs">({log.company_name})</span>
                                                                )}
                                                            </div>
                                                            <span className="text-xs mt-1 sm:mt-0">{formatDate(log.createAt)}</span>
                                                        </div>
                                                        <div className="p-3 md:p-4">
                                                            <div className="mb-3">
                                                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Request URL</h4>
                                                                <p className="text-xs md:text-sm text-gray-800 dark:text-gray-200 break-all">{log.request}</p>
                                                            </div>
                                                            <div>
                                                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Response</h4>
                                                                <pre className="text-xs md:text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 p-2 rounded overflow-auto max-h-60">
                                                                    {formatLogResponse(log.response)}
                                                                </pre>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Complaint & Remark Section */}
                            {(orderData.order_info.isComplaint === '1' || orderData.complaint?.length > 0) && (
                                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mt-6">
                                    <div className="p-4 md:p-6">
                                        <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white border-b pb-2 mb-4">Complaint Details</h2>
                                        <div className="space-y-4">
                                            {orderData.complaint?.map((complaint, index) => {
                                                const getComplaintStatusColor = (status) => {
                                                    switch (status.toLowerCase()) {
                                                        case 'ongoing':
                                                            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
                                                        case 'pending':
                                                            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
                                                        case 'resolved':
                                                            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
                                                        case 'rejected':
                                                            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
                                                        default:
                                                            return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
                                                    }
                                                };

                                                return (
                                                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                                        <div className="px-3 py-2 md:px-4 md:py-3 bg-gray-50 dark:bg-gray-700 flex flex-col sm:flex-row sm:justify-between sm:items-center">
                                                            <div className="flex items-center space-x-2">
                                                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                                                                <div>
                                                                    <span className="font-medium text-sm">Complaint ID: {complaint.com_id}</span>
                                                                    {complaint.com_id_provider_end && (
                                                                        <span className="text-xs ml-2 block sm:inline">(Provider ID: {complaint.com_id_provider_end})</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center mt-2 sm:mt-0">
                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getComplaintStatusColor(complaint.status)}`}>
                                                                    {complaint.status}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="p-3 md:p-4">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                                                <div>
                                                                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Date & Time</h4>
                                                                    <p className="text-xs md:text-sm text-gray-800 dark:text-gray-200">
                                                                        {formatDate(complaint.date)} at {formatDate(complaint.time_com)}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Reason</h4>
                                                                    <p className="text-xs md:text-sm text-gray-800 dark:text-gray-200">{complaint.reason}</p>
                                                                </div>
                                                            </div>
                                                            <div className="mb-3">
                                                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Message</h4>
                                                                <p className="text-xs md:text-sm text-gray-800 dark:text-gray-200">{complaint.message}</p>
                                                            </div>
                                                            <div>
                                                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Remark</h4>
                                                                <p className="text-xs md:text-sm text-gray-800 dark:text-gray-200">{complaint.remark}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Fallback for old isComplaint flag */}
                                            {orderData.order_info.isComplaint === '1' && !orderData.complaint?.length && (
                                                <div className="bg-red-50 dark:bg-red-900/20 p-3 md:p-4 rounded-lg">
                                                    <div className="flex items-center">
                                                        <XCircleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                                                        <p className="font-medium text-red-800 dark:text-red-200 text-sm md:text-base">This order has a complaint</p>
                                                    </div>
                                                    {orderData.order_info.remark && (
                                                        <div className="mt-2">
                                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Remark</p>
                                                            <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap text-sm md:text-base">{orderData.order_info.remark}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </main>
    </div>
    {/* Edit Modal */}
    <AnimatePresence>
        {editModalOpen && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                onClick={() => setEditModalOpen(false)}
            >
                <motion.div
                    initial={{ scale: 0.9, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, y: 20 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Fixed Header */}
                    <div className="flex justify-between items-center border-b dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-800 z-10">
                        <h3 className="text-lg font-semibold dark:text-white">Update Order</h3>
                        <button
                            onClick={() => setEditModalOpen(false)}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="overflow-y-auto flex-1 p-4">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status*</label>
                                <select
                                    name="status"
                                    value={editFormData.status}
                                    onChange={handleEditChange}
                                    className={`w-full px-3 py-2 border ${editFormErrors.status ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white`}
                                    required
                                >
                                    <option value="">Select Status</option>
                                    <option value="1">Success</option>
                                    <option value="2">Failed</option>
                                </select>
                                {editFormErrors.status && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{editFormErrors.status}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transaction ID*</label>
                                <input
                                    type="text"
                                    name="transaction_id"
                                    value={editFormData.transaction_id}
                                    onChange={handleEditChange}
                                    className={`w-full px-3 py-2 border ${editFormErrors.transaction_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white`}
                                    placeholder="Enter transaction ID"
                                />
                                {editFormErrors.transaction_id && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{editFormErrors.transaction_id}</p>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Fixed Footer */}
                    <div className="flex justify-end space-x-3 p-4 border-t dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
                        <button
                            type="button"
                            onClick={() => setEditModalOpen(false)}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                                    <span>Updating...</span>
                                </>
                            ) : (
                                <>
                                    <CheckIcon className="h-5 w-5" />
                                    <span>Save Changes</span>
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>

    <ToastContainer />
</div>
    );
};

export default OrderDetails;